import { useState, useEffect } from 'react';
import {
  Button,
  TextInput,
  Modal,
  Group,
  Stack,
  Text,
  ActionIcon,
  Paper,
  Badge,
  ScrollArea,
  Box,
} from '@mantine/core';
import {
  IconTrash,
  IconEdit,
  IconPlus,
  IconX,
  IconDatabase,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import classes from './LocalStorageManager.module.css';

interface LocalStorageItem {
  key: string;
  value: string;
}

export function LocalStorageManager() {
  const [opened, { open, close }] = useDisclosure(false);
  const [
    deleteConfirmOpened,
    { open: openDeleteConfirm, close: closeDeleteConfirm },
  ] = useDisclosure(false);
  const [
    clearAllConfirmOpened,
    { open: openClearAllConfirm, close: closeClearAllConfirm },
  ] = useDisclosure(false);
  const [items, setItems] = useState<LocalStorageItem[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);

  const loadItems = () => {
    const allItems: LocalStorageItem[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        try {
          const value = localStorage.getItem(key) || '';
          allItems.push({ key, value });
        } catch (error) {
          console.error(`Error reading key ${key}:`, error);
        }
      }
    }
    setItems(allItems);
  };

  useEffect(() => {
    loadItems();
    // Listen for storage changes from other tabs
    const handleStorageChange = () => {
      loadItems();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Keyboard shortcut: Option + Shift + I
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Debug logging (can be removed later)
      if (
        process.env.NODE_ENV === 'development' &&
        (event.altKey || event.metaKey) &&
        event.shiftKey
      ) {
        console.log('Shortcut attempt:', {
          key: event.key,
          code: event.code,
          altKey: event.altKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
        });
      }

      // Option + Shift + I
      // Use event.code for physical key detection (more reliable with modifiers)
      const isCorrectKey = event.code === 'KeyI';

      // On macOS, Option key = altKey (not metaKey, which is Command)
      const hasOption = event.altKey;
      const hasShift = event.shiftKey;

      // Ensure no other modifiers are pressed (Command, Ctrl, etc.)
      const onlyModifiers =
        hasOption && hasShift && !event.ctrlKey && !event.metaKey;

      if (isCorrectKey && onlyModifiers) {
        // Prevent default browser behavior and any other handlers
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        open();
        return false;
      }
    };

    // Use capture phase to catch the event before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  const handleDelete = (key: string) => {
    setKeyToDelete(key);
    openDeleteConfirm();
  };

  const confirmDelete = () => {
    if (keyToDelete) {
      localStorage.removeItem(keyToDelete);
      loadItems();
      closeDeleteConfirm();
      setKeyToDelete(null);
      notifications.show({
        title: 'Deleted',
        message: `Key "${keyToDelete}" has been deleted`,
        color: 'green',
      });
    }
  };

  const handleEdit = (key: string, value: string) => {
    setEditingKey(key);
    setEditValue(value);
  };

  const handleSaveEdit = () => {
    if (editingKey) {
      try {
        localStorage.setItem(editingKey, editValue);
        setEditingKey(null);
        setEditValue('');
        loadItems();
        notifications.show({
          title: 'Saved',
          message: `Key "${editingKey}" has been updated`,
          color: 'green',
        });
      } catch (error) {
        console.error('Error saving:', error);
        notifications.show({
          title: 'Error',
          message: 'Error saving value. It might be too large.',
          color: 'red',
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleAdd = () => {
    if (newKey.trim() && newValue.trim()) {
      try {
        localStorage.setItem(newKey.trim(), newValue.trim());
        setNewKey('');
        setNewValue('');
        loadItems();
        notifications.show({
          title: 'Added',
          message: `Key "${newKey.trim()}" has been added`,
          color: 'green',
        });
      } catch (error) {
        console.error('Error adding:', error);
        notifications.show({
          title: 'Error',
          message:
            'Error adding item. Key might already exist or value might be too large.',
          color: 'red',
        });
      }
    }
  };

  const handleClearAll = () => {
    openClearAllConfirm();
  };

  const confirmClearAll = () => {
    localStorage.clear();
    loadItems();
    closeClearAllConfirm();
    notifications.show({
      title: 'Cleared',
      message: 'All localStorage items have been cleared',
      color: 'green',
    });
  };

  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <>
      {isDevelopment && (
        <Button
          className={classes.toggleButton}
          onClick={open}
          leftSection={<IconDatabase size={16} />}
          size="sm"
          variant="filled"
          color="blue"
        >
          localStorage
        </Button>
      )}

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text fw={600}>LocalStorage Manager</Text>
            <Badge color="blue" variant="light">
              {items.length} items
            </Badge>
          </Group>
        }
        size="lg"
        styles={{
          body: { padding: 0 },
        }}
      >
        <Stack gap={0}>
          {/* Add new item */}
          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Add New Item
              </Text>
              <Group gap="xs" align="flex-end">
                <TextInput
                  placeholder="Key"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  style={{ flex: 1 }}
                  size="sm"
                />
                <TextInput
                  placeholder="Value"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  style={{ flex: 1 }}
                  size="sm"
                />
                <Button
                  onClick={handleAdd}
                  leftSection={<IconPlus size={16} />}
                  size="sm"
                  disabled={!newKey.trim() || !newValue.trim()}
                >
                  Add
                </Button>
              </Group>
            </Stack>
          </Paper>

          {/* Items list */}
          <ScrollArea h={400}>
            <Stack gap={0}>
              {items.length === 0 ? (
                <Box p="md" ta="center">
                  <Text c="dimmed" size="sm">
                    No items in localStorage
                  </Text>
                </Box>
              ) : (
                items.map((item) => (
                  <Paper
                    key={item.key}
                    p="md"
                    withBorder
                    style={{ borderTop: 'none' }}
                  >
                    {editingKey === item.key ? (
                      <Stack gap="xs">
                        <TextInput
                          label={
                            <Text size="xs" fw={500}>
                              {item.key}
                            </Text>
                          }
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          size="sm"
                        />
                        <Group gap="xs" justify="flex-end">
                          <Button
                            onClick={handleCancelEdit}
                            variant="subtle"
                            size="xs"
                            leftSection={<IconX size={14} />}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSaveEdit}
                            size="xs"
                            leftSection={<IconEdit size={14} />}
                          >
                            Save
                          </Button>
                        </Group>
                      </Stack>
                    ) : (
                      <Group justify="space-between" align="flex-start">
                        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate>
                            {item.key}
                          </Text>
                          <Text
                            size="xs"
                            c="dimmed"
                            style={{
                              wordBreak: 'break-word',
                              maxHeight: '60px',
                              overflow: 'auto',
                            }}
                          >
                            {item.value.length > 100
                              ? `${item.value.substring(0, 100)}...`
                              : item.value || '(empty)'}
                          </Text>
                        </Stack>
                        <Group gap="xs">
                          <ActionIcon
                            onClick={() => handleEdit(item.key, item.value)}
                            variant="subtle"
                            color="blue"
                            size="sm"
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            onClick={() => handleDelete(item.key)}
                            variant="subtle"
                            color="red"
                            size="sm"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    )}
                  </Paper>
                ))
              )}
            </Stack>
          </ScrollArea>

          {/* Actions */}
          <Paper p="md" withBorder style={{ borderTop: 'none' }}>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Total: {items.length} items
              </Text>
              <Button
                onClick={handleClearAll}
                variant="light"
                color="red"
                size="xs"
                leftSection={<IconTrash size={14} />}
              >
                Clear All
              </Button>
            </Group>
          </Paper>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={closeDeleteConfirm}
        title="Confirm Delete"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete <strong>"{keyToDelete}"</strong>?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteConfirm}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Clear All Confirmation Modal */}
      <Modal
        opened={clearAllConfirmOpened}
        onClose={closeClearAllConfirm}
        title="Confirm Clear All"
        centered
      >
        <Stack gap="md">
          <Text c="red" fw={500}>
            Are you sure you want to clear ALL localStorage?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone. All {items.length} items will be
            permanently deleted.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeClearAllConfirm}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmClearAll}>
              Clear All
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
